import { reactive, ref, shallowRef, toRaw, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useStorage } from '@vueuse/core'
import { STORAGE_KEYS } from '@/constants'
import WebSocketService from '@/services/websocketService'
import { i18nRender } from '@/locales'
import type { QuotePayload } from '@/types/quote'
import { useAccountStore } from '@/store'
import { useSocketLogin } from '@/composables'
import type { OpenOrderPayload, ClosePayload } from '@/views/(public)/trading/types'
import { SOCKET_CMD_TIMEOUT } from '@/constants'

export const PINNED_KEY = '__pinned__'
const { SOCKET_TOKEN } = STORAGE_KEYS
const MIN_LOGIN_INTERVAL_MS = 1500

const wsService = WebSocketService.getInstance()

const useSocketStore = defineStore('socket', () => {
  const accountStore = useAccountStore()
  const { selectedLoginid } = storeToRefs(accountStore)
  const { socketLogin } = useSocketLogin()

  // hidePanel模块级订阅池与全局固定订阅
  // 各模块用独立 Set 维护自身需要的 symbols，Store 统一求并集，避免互相干扰
  const moduleSubscriptions = reactive(new Map<string, Set<string>>())

  //单飞(only-one-in-flight)登录与最小时间间隔，避免并发登录风暴
  const loginInFlight = ref<Promise<void> | null>(null)
  let lastLoginAt = 0

  const socketToken = useStorage(SOCKET_TOKEN, '')
  const isSocketLoggedIn = ref(false)

  let onOpenRef: (() => Promise<void>) | null = null
  let onErrorRef: ((e: any) => void) | null = null
  let onCloseRef: ((e: any) => void) | null = null

  let rafId: number | null = null
  // 报价相关
  const lastQuoteMap = reactive(new Map<string, QuotePayload>())
  const prevQuoteSnapshot = reactive(new Map<string, { ask: number; bid: number }>())
  // 全局变更 tick（任意 symbol 变化时递增）
  const quoteTick = ref(0)
  // 本帧发生变化的 symbol 集合（供批处理使用）
  const changedSymbols = reactive(new Set<string>())
  // 资金相关
  const pendingAccountMargin = new Map<string, any>()

  // 订单相关
  let syncRafId: number | null = null
  const orderOperationEvents = shallowRef<any[]>([])
  const orderOperateResult: any[] = []

  // 订阅相关
  let subRafId: number | null = null
  let pendingSub = new Set<string>()

  // 挂起请求
  const pendingRequests = new Map<
    string,
    { resolve: (d: any) => void; reject: (e: any) => void; timeoutId: number }
  >()

  const computeDesiredSet = (): Set<string> => {
    const desired = new Set<string>()
    moduleSubscriptions.forEach((set) => set.forEach((s) => desired.add(s)))
    return desired
  }

  const scheduleSync = () => {
    if (syncRafId != null) return
    syncRafId = requestAnimationFrame(() => {
      syncRafId = null
      syncActiveSubscriptions()
    })
  }

  const setsEqual = (a?: Set<string>, b?: Set<string>) => {
    if (!a || !b) return false
    if (a.size !== b.size) return false
    for (const v of a) if (!b.has(v)) return false
    return true
  }

  // hidePanel统一差量同步 desired ↔ current，支持批量/幂等/缓存清理
  const syncActiveSubscriptions = () => {
    const desired = computeDesiredSet()
    const current = wsService.getActiveSubs() // Set<string>

    // 快速路径——完全一致时直接返回，避免无谓开销
    if (desired.size === current.size) {
      let identical = true
      for (const s of desired) {
        if (!current.has(s)) {
          identical = false
          break
        }
      }
      if (identical) return
    }

    // 计算差集，退订 current - desired，订阅 desired - current
    const toUnsub: string[] = []
    current.forEach((s) => {
      if (!desired.has(s)) toUnsub.push(s)
    })

    const toSub: string[] = []
    desired.forEach((s) => {
      if (!current.has(s)) toSub.push(s)
    })

    // 复杂逻辑：优先批量，减少瞬时报文；不再用 'in' 做类型收窄
    if (toUnsub.length > 0) {
      wsService.batchUnsubscribe(toUnsub)
      toUnsub.forEach((s) => {
        lastQuoteMap.delete(s)
        prevQuoteSnapshot.delete(s)
      })
    }

    if (toSub.length > 0) {
      wsService.batchSubscribe(toSub)
    }
  }

  const setPinned = (symbol?: string) => {
    if (symbol) updateModulePool(PINNED_KEY, [symbol])
    else removeModulePool(PINNED_KEY)
  }

  const updateModulePool = (moduleKey: string, symbols: readonly string[]) => {
    if (!symbols || symbols.length === 0) {
      if (moduleSubscriptions.has(moduleKey)) {
        moduleSubscriptions.delete(moduleKey)
        scheduleSync()
      }
      return
    }
    const next = new Set(symbols)
    const prev = moduleSubscriptions.get(moduleKey)
    if (setsEqual(prev, next)) return // 没变就不调度
    moduleSubscriptions.set(moduleKey, next)
    scheduleSync()
  }

  // 增量打补丁（可选：有些模块喜欢 add/remove）
  const patchModulePool = (
    moduleKey: string,
    patch: { add?: string[]; remove?: string[] } = {},
  ) => {
    const set = new Set(moduleSubscriptions.get(moduleKey) ?? [])
    let changed = false
    if (patch.add)
      for (const s of patch.add)
        if (!set.has(s)) {
          set.add(s)
          changed = true
        }
    if (patch.remove)
      for (const s of patch.remove)
        if (set.delete(s)) {
          changed = true
        }
    if (!changed) return
    if (set.size === 0) moduleSubscriptions.delete(moduleKey)
    else moduleSubscriptions.set(moduleKey, set)
    scheduleSync()
  }

  // 模块卸载/隐藏时清空池
  const removeModulePool = (moduleKey: string) => {
    if (moduleSubscriptions.has(moduleKey)) {
      moduleSubscriptions.delete(moduleKey)
      scheduleSync()
    }
  }

  const unsubscribeAll = () => {
    wsService.unsubscribeAll()
    lastQuoteMap.clear()
    prevQuoteSnapshot.clear()
  }

  // 【修改】在 auth:ok 或 connect 成功后，恢复当前 UI 的订阅
  async function resubscribeAndWarmupSpecs() {
    // 复杂逻辑：统一由 syncActiveSubscriptions 恢复（避免 UI 空窗）
    scheduleSync()
    // …若还需拉取规格，可在此补充（保持你原注释结构即可）
  }

  const sendCmd = <T = any>(
    body: OpenOrderPayload | ClosePayload,
    timeout = SOCKET_CMD_TIMEOUT,
  ): Promise<T> => {
    if (!wsService.isConnected()) {
      return Promise.reject(new Error(i18nRender('trading.messages.websocketNotConnected')))
    }

    // 已连接但未完成鉴权时，先确保鉴权（可防止“已连未登”时发送 cmd 失败）
    const ensureAuth = (async () => {
      if (!isSocketLoggedIn.value) {
        await ensureSocketAuth()
      }
    })()

    const seq = body.seq
    return new Promise<T>(async (resolve, reject) => {
      try {
        await ensureAuth
      } catch (e) {
        return reject(e as any)
      }
      const timeoutId = window.setTimeout(() => {
        pendingRequests.delete(seq)
        reject(new Error(i18nRender('trading.messages.requestTimeout')))
      }, timeout)

      pendingRequests.set(seq, { resolve, reject, timeoutId })
      try {
        wsService.send('cmd:' + JSON.stringify(body))
      } catch (err) {
        clearTimeout(timeoutId)
        pendingRequests.delete(seq)
        reject(err)
      }
    })
  }

  const setSocketToken = (token: string) => {
    socketToken.value = token
  }

  const rejectAllPending = (reason = i18nRender('trading.messages.websocketClosed')) => {
    pendingRequests.forEach(({ reject, timeoutId }) => {
      window.clearTimeout(timeoutId)
      try {
        reject(new Error(reason))
      } catch {}
    })
    pendingRequests.clear()
  }

  const scheduleFrame = () => {
    if (!rafId) {
      rafId = requestAnimationFrame(flushAll)
    }
  }

  const flushAll = () => {
    // 1、处理行情 - 在 RAF 中检测价格变化并批量标记
    if (lastQuoteMap.size > 0) {
      let anyChanged = false
      lastQuoteMap.forEach((quote, symbol) => {
        const prev = prevQuoteSnapshot.get(symbol)
        const changed = !prev || (prev.ask && prev.ask !== quote.ask) || prev.bid !== quote.bid
        if (changed) {
          changedSymbols.add(symbol)
          anyChanged = true
          prevQuoteSnapshot.set(symbol, { ask: quote.ask, bid: quote.bid })
        }
      })
      if (anyChanged) quoteTick.value++
    }
    // 2、处理订单操作
    if (orderOperateResult.length > 0) {
      const batch = orderOperateResult.splice(0)
      orderOperationEvents.value = batch
    }
    // 3、统一处理账户资金更新（仅提交“当前选中账户”的最新快照；其他账户继续累计）
    if (pendingAccountMargin.size > 0) {
      // 仅选择当前选中账户（selectedAccount）的 LOGIN 进行更新；其他 LOGIN 暂存
      const selectedLogin = selectedLoginid.value

      if (selectedLogin != null) {
        const key = String(selectedLogin)
        const latestPayload = pendingAccountMargin.get(key)

        if (latestPayload) {
          // 只下发当前选中账户的“最后一次”资金快照
          accountStore.updateAccountWithSocketInfo(latestPayload)
          // 仅删除已处理的账户，其它账户的事件继续累计（避免无意义渲染）
          pendingAccountMargin.delete(key)
        }
      }
      // 未选中账户时，不做资金面板刷新；事件继续累计到下一帧或等账户就绪/切换后处理
    }

    rafId = null
  }

  const ensureSocketAuth = async () => {
    if (isSocketLoggedIn.value) return

    const now = Date.now()
    const due = Math.max(0, MIN_LOGIN_INTERVAL_MS - (now - lastLoginAt))
    if (due > 0) {
      await new Promise((r) => setTimeout(r, due))
    }

    if (loginInFlight.value) {
      await loginInFlight.value
      return
    }

    lastLoginAt = Date.now()
    loginInFlight.value = (async () => {
      try {
        await socketLogin()
      } finally {
        loginInFlight.value = null
      }
    })()

    await loginInFlight.value
  }

  const triggerSocketAuth = () => {
    // 非阻塞地触发，错误只记录日志
    ensureSocketAuth().catch((err) => console.warn('[trade] triggerSocketAuth failed:', err))
  }

  const clearChangedSymbols = () => {
    changedSymbols.clear()
  }

  async function handleIncomingData(raw: unknown) {
    let needSchedule = false
    let txt = ''
    if (typeof raw === 'string') {
      txt = raw.trim()
    } else if (raw instanceof ArrayBuffer) {
      txt = new TextDecoder().decode(raw)
    } else return

    if (txt.startsWith('#{')) {
      try {
        const jsonStr = txt.slice(1)
        const payload = JSON.parse(jsonStr)

        if (payload.event === 'accountmargin') {
          const data = payload.data || {}
          if (data.LOGIN) {
            pendingAccountMargin.set(String(data.LOGIN), data)
          }
          needSchedule = true
          return // 不进入订单处理流水
        }

        // 处理 cmd response，把结果分发给对应的 Promise
        if (payload.event === 'response' && payload.data?.seq) {
          const { seq, code, message, data } = payload.data
          if (message === 'unauthorized') {
            // 统一走单飞鉴权，避免并发登录
            isSocketLoggedIn.value = false
            triggerSocketAuth()
          }
          const pending = pendingRequests.get(seq)
          if (pending) {
            window.clearTimeout(pending.timeoutId)
            pendingRequests.delete(seq)
            if (code === 0) pending.resolve(data)
            else pending.reject(new Error(message || i18nRender('trading.messages.requestFailed')))
          }
          // response 不需要刷新 UI
          return
        }
        if (
          payload.event === 'positionadd' ||
          payload.event === 'positiondelete' ||
          payload.event === 'positionupdate'
        ) {
          // 订单增/删/改 事件
          orderOperateResult.push(payload)
          needSchedule = true
        }
      } catch (e) {
        console.warn(i18nRender('trading.messages.parseOrderFailed') + ':', e, txt)
      }
    } else if (txt.startsWith('auth')) {
      // 鉴权文本帧：只维护登录态，不刷新 UI
      if (txt === 'auth:ok') {
        isSocketLoggedIn.value = true
        resubscribeAndWarmupSpecs().catch((e) => console.warn('[trade] auth-ok warmup fail:', e))
      } else if (txt === 'auth:Invalid token' || txt.includes('token expired')) {
        isSocketLoggedIn.value = false
        triggerSocketAuth()
      }
    } else {
      // 报价
      const parts = txt.split(/\s+/)
      if (parts.length !== 8) return
      const [symbol, askStr, bidStr, tsStr, open, high, low, pre] = parts

      const payload: QuotePayload = {
        ask: +askStr,
        bid: +bidStr,
        ts: +tsStr,
        dayOpen: +open,
        dayHigh: +high,
        dayLow: +low,
        preClose: +pre,
      }

      lastQuoteMap.set(symbol, payload)
      needSchedule = true
    }
    if (needSchedule) scheduleFrame()
  }

  // async function resubscribeAndWarmupSpecs() {
  // 订阅 pinned（selectedTab）与 tabs 中的合约，避免空白期
  // const targets = new Set<string>()
  // if (selectedTab.value) targets.add(selectedTab.value.symbol)
  // tabs.value.forEach((t) => targets.add(t.symbol))
  // if (targets.size > 0) {
  //   subscribe(Array.from(targets), 'update')
  // }
  // // 兜底：selectedTab 未变更时，原有 watch 不会触发，这里强制拉一次规格
  // if (selectedTab.value) {
  //   if (selectedAccount.value) {
  //     try {
  //       await symbolInfoStore.fetchSpecs([selectedTab.value], selectedAccount.value)
  //     } catch (e) {
  //       console.warn('[trade] resubscribeAndWarmupSpecs fetchSpecs error:', e)
  //     }
  //   } else {
  //     // 账户尚未就绪则延迟一次
  //     if (!waitAccountStop) {
  //       const stop = watch(
  //         () => selectedAccount.value,
  //         (acc) => {
  //           if (!acc || !selectedTab.value) return
  //           symbolInfoStore
  //             .fetchSpecs([selectedTab.value], selectedAccount.value)
  //             .catch((err) => console.warn('[trade] deferred warmup fetchSpecs error:', err))
  //             .finally(() => {
  //               stop()
  //               waitAccountStop = null
  //             })
  //         },
  //         { immediate: false },
  //       )
  //       waitAccountStop = stop
  //     }
  //   }
  // }
  // }

  const resetSocketState = () => {
    socketToken.value = ''
    isSocketLoggedIn.value = false
    loginInFlight.value = null
    lastLoginAt = 0
  }

  const clearRaf = () => {
    if (syncRafId != null) {
      cancelAnimationFrame(syncRafId)
      syncRafId = null
    }
    if (subRafId != null) {
      cancelAnimationFrame(subRafId)
      subRafId = null
    }
    if (rafId != null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  const unbindOnce = () => {
    if (onOpenRef) {
      wsService.removeEventListener('open', onOpenRef)
      onOpenRef = null
    }
    if (onErrorRef) {
      wsService.removeEventListener('error', onErrorRef)
      onErrorRef = null
    }
    if (onCloseRef) {
      wsService.removeEventListener('close', onCloseRef)
      onCloseRef = null
    }
  }

  const destroySocket = async () => {
    resetSocketState()
    clearRaf()
    wsService.removeEventListener('message', handleIncomingData)
    unbindOnce()
    try {
      await wsService.destroy()
    } catch (e) {
      console.warn('[socket] close error:', e)
    }
  }

  const _clearBusinessCaches = (opts: { dropDesired?: boolean } = {}) => {
    const { dropDesired = false } = opts
    // A) 订阅意图
    if (dropDesired) {
      moduleSubscriptions.clear() // 真正切账号/退出：清空期望订阅集
    } else {
    }

    // B) 本地缓存（行情/资金/订单等）
    lastQuoteMap.clear()
    prevQuoteSnapshot.clear()
    quoteTick.value = 0
    changedSymbols.clear()
    pendingAccountMargin.clear()
    orderOperationEvents.value = []

    // 若你有其它业务缓存、索引、Map/Set，也在这里清
  }

  // === 对外：全量重置（切账号/登出时用）：先断传输，再清业务 ===
  const reset = async (opts: { dropDesired?: boolean } = {}) => {
    resetSocketState()
    _clearBusinessCaches(opts)
    clearRaf()
  }

  const connectSocket = (url: string) => {
    return new Promise<boolean>((resolve, reject) => {
      onOpenRef = async () => {
        try {
          // 置为未登录状态，等待 auth:ok 后再置为 true（在 handleIncomingData 中处理）
          isSocketLoggedIn.value = false
          await ensureSocketAuth()
          await resubscribeAndWarmupSpecs()
          resolve(true)
        } catch (error) {
          console.error(error)
          resolve(true)
        }
      }

      // 错误监听；发生错误时解绑监听并 reject
      onErrorRef = (error: any) => {
        unbindOnce()
        reject(error)
      }

      onCloseRef = () => {
        isSocketLoggedIn.value = false
        // 关闭时拒绝所有挂起请求并清理监听
        rejectAllPending()
        unbindOnce()
      }

      // 先绑定监听再 connect，避免极端情况下 open 先触发而监听尚未就绪
      wsService.addEventListener('message', handleIncomingData)
      wsService.addEventListener('open', onOpenRef!)
      wsService.addEventListener('error', onErrorRef!)
      wsService.addEventListener('close', onCloseRef!)

      wsService.connect(url)
    })
  }

  return {
    socketToken,
    quoteTick,
    changedSymbols,
    lastQuoteMap,
    prevQuoteSnapshot,
    orderOperationEvents,
    orderOperateResult,
    pendingAccountMargin,
    pendingRequests,
    isSocketLoggedIn,
    moduleSubscriptions,
    setPinned,
    updateModulePool,
    patchModulePool,
    removeModulePool,
    sendCmd,
    setSocketToken,
    connectSocket,
    destroySocket,
    unsubscribeAll,
    ensureSocketAuth,
    triggerSocketAuth,
    resubscribeAndWarmupSpecs,
    reset,
    scheduleFrame,
    flushAll,
    clearChangedSymbols,
  }
})

export { useSocketStore }
export default useSocketStore
